/**
 * Derives the two file names a browser-zipped XBRL download needs: the `.zip`
 * the user saves, and the `.xml` entry inside it.
 *
 * The source name varies by design. `ConversionData.xbrlFileName` normally holds
 * the backend's standardised `SSM_[FS-MFRS|FS-MPERS]_[BRN]_[yyyyMMdd]` (no
 * extension), but falls back to the ORIGINAL UPLOAD name (e.g. `FS.xlsx`) when
 * Filing Info is incomplete — no `SSM_` prefix, and an extension that must come
 * off. That fallback is why downloads used to be named `FS.xlsx.xml`.
 *
 * Per product decision the `SSM_` prefix is dropped from the `.zip` only; the
 * `.xml` inside keeps it, because that is the file actually filed with SSM.
 */

/**
 * Extensions the fallback source name can realistically carry. Deliberately a
 * CLOSED LIST rather than a greedy `/\.[^.]+$/`: a filing named
 * `ABC Sdn. Bhd. FS` would otherwise lose ` Bhd. FS`.
 */
const SOURCE_EXTENSION_PATTERN = /\.(xlsx?|docx?|pdf|zip|xml)$/i;

/**
 * Illegal on Windows filesystems, and a path-traversal vector once the name is
 * used as a zip entry.
 *
 * NOTE the space is deliberately NOT in this set — `Accounts 2025.pdf` keeps its
 * space. Control characters are handled separately by `stripControlChars` rather
 * than by a character-class range, which keeps this file free of unicode escapes.
 */
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g;

/** Strips C0 controls and DEL, which are illegal in both filenames and zip entries. */
function stripControlChars(value: string): string {
  return Array.from(value)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
}

/** Leading/trailing dots produce hidden or malformed names (`.xml`, `name..xml`). */
const EDGE_DOTS = /^\.+|\.+$/g;

const SSM_PREFIX = /^SSM_/;

/**
 * Thrown when neither `xbrlFileName` nor `fileName` yields a usable name.
 *
 * This cannot happen in a valid flow — `ConversionData.fileName` is typed
 * non-optional — so it signals a data defect upstream and is surfaced rather
 * than papered over with a synthesised name. The caller attaches the conversion
 * id when logging; this utility is deliberately id-unaware so it stays pure.
 */
export class XbrlDownloadNameError extends Error {
  constructor() {
    super(
      'Cannot derive an XBRL download file name: both xbrlFileName and fileName are empty.'
    );
    this.name = 'XbrlDownloadNameError';
  }
}

export interface XbrlDownloadNames {
  /** What the user saves, e.g. `FS-MFRS_12345679_20250211.zip`. */
  readonly zipName: string;
  /** The single entry inside the zip, e.g. `SSM_FS-MFRS_12345679_20250211.xml`. */
  readonly xmlName: string;
}

export function deriveXbrlDownloadNames(
  xbrlFileName: string | undefined,
  fileName: string | undefined
): XbrlDownloadNames {
  const source = xbrlFileName?.trim() || fileName?.trim() || '';
  if (!source) throw new XbrlDownloadNameError();

  const base = stripControlChars(source)
    .replace(SOURCE_EXTENSION_PATTERN, '')
    .replace(ILLEGAL_FILENAME_CHARS, '_')
    .replace(EDGE_DOTS, '')
    .trim();

  if (!base) throw new XbrlDownloadNameError();

  return {
    xmlName: `${base}.xml`,
    zipName: `${base.replace(SSM_PREFIX, '')}.zip`,
  };
}
